/* eslint-disable complexity */
import React, {FC, useCallback, useMemo, useState} from 'react';

import {GatewayError} from '@gravity-ui/gateway';
import {ArrowUpFromSquare} from '@gravity-ui/icons';
import {DataSource, QuestionType, ValidationType} from 'common/constants/survey';
import {
    CaptchaQuestion as CaptchaQuestionModel,
    CaptchaValue,
    ChoiceQuestion,
    CommentQuestion,
    DateRangeValue,
    DropdownQuestion as DropdownQuestionModel,
    FileInfo,
    FileValue,
    MatrixQuestion as MatrixQuestionModel,
    MatrixValue,
    MaxCountFileValidation,
    MaxDateValidation,
    MaxSizeFileValidation,
    MinDateValidation,
    PaymentQuestion as PaymentQuestionModel,
    StringQuestion,
    SuggestItem,
    SuggestQuestion as SuggestQuestionModel,
} from 'common/models/survey';
import i18n from 'i18n';
import {useFormContext} from 'react-hook-form';
import {sdk} from 'ui/utils/sdk';

import BooleanQuestion from '../BooleanQuestion';
import CaptchaQuestion from '../CaptchaQuestion';
import CheckboxQuestion from '../CheckboxQuestion';
import DateQuestion from '../DateQuestion';
import DropdownQuestion from '../DropdownQuestion';
import FileQuestion from '../FileQuestion';
import IntegerQuestion from '../IntegerQuestion';
import MatrixQuestion from '../MatrixQuestion';
import PaymentQuestion from '../PaymentQuestion';
import QuestionLayout, {QuestionLayoutType} from '../QuestionLayout';
import {QuestionMarkupItem} from '../QuestionMarkup';
import RadioQuestion from '../RadioQuestion';
import {RadioQuestionView} from '../RadioQuestion/RadioQuestion.types';
import SuggestQuestion from '../SuggestQuestion';
import TextQuestion from '../TextQuestion';

import {QuestionSwitchProps} from './QuestionSwitch.types';

const QuestionSwitch: FC<QuestionSwitchProps> = ({
    question,
    surveyId,
    className,
    field,
    fieldState,
}) => {
    const [error, setError] = useState<string>();

    const {watch} = useFormContext();

    const commonProps = useMemo(
        () => ({
            error: fieldState.error?.message ?? error,
            className,
            label: question.label,
            comment: question.comment,
            image: question.image?.links,
            required: question.validations?.some(({type}) => type === 'required') ?? false,
            name: field.name,
            ref: field.ref,
            id: question.id,
        }),
        [
            className,
            error,
            field.name,
            field.ref,
            fieldState.error?.message,
            question.comment,
            question.id,
            question.image?.links,
            question.label,
            question.validations,
        ],
    );

    // suggest question from wiki table or yt table may have filtering by the value of another question (parent)
    const parentQuestions = (question as SuggestQuestionModel).parent;
    let parentsValues: string[] | undefined;
    if (parentQuestions) {
        parentsValues = watch(String(parentQuestions));
    }

    const getItems = useCallback(
        (text?: string, id?: string) => {
            return sdk.form
                .getSuggest({
                    question: question.id,
                    id,
                    text,
                    parents: parentsValues,
                    surveyId,
                })
                .then((result) => {
                    setError('');
                    return result;
                })
                .catch(() => {
                    setError(i18n('survey-page', 'error-load-data'));
                    return [] as SuggestItem[];
                });
        },
        [parentsValues, question.id, surveyId],
    );

    const getItem = useCallback((id: string) => getItems(undefined, id), [getItems]);

    const upload = useCallback((file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('surveyId', String(surveyId));
        return sdk.form
            .uploadFile(formData)
            .then<FileValue>((info: FileInfo) => {
                setError('');
                return {
                    loading: false,
                    ...info,
                };
            })
            .catch((errorData: GatewayError) => {
                setError(
                    errorData.details?.message
                        ? String(errorData.details?.message)
                        : i18n('survey-page', 'error-file-upload'),
                );
                throw error;
            });
    }, []);

    const onChangeRadio = useCallback((value: string) => field.onChange([value]), [field]);

    const onChangeMatrix = useCallback(
        (matrix: Record<string, string>) =>
            field.onChange(
                Object.entries(matrix).map(([row, column]) => ({
                    row,
                    column,
                })),
            ),
        [field],
    );

    const onChangeInteger = useCallback(
        (value: string) => {
            if (!value) {
                return field.onChange('');
            }

            return field.onChange(Number(value));
        },
        [field],
    );

    const onRefreshCaptcha = useCallback(() => {
        return sdk.form.postCaptcha({mode: (question as CaptchaQuestionModel).mode});
    }, [question]);

    switch (question.type) {
        case QuestionType.Stars:
            return (
                <RadioQuestion
                    {...commonProps}
                    value={(field.value as string[])?.[0]}
                    onUpdate={onChangeRadio}
                    items={(question as ChoiceQuestion).items}
                    view={RadioQuestionView.Stars}
                />
            );
        case QuestionType.Radio:
            return (
                <RadioQuestion
                    {...commonProps}
                    value={(field.value as string[])?.[0]}
                    onUpdate={onChangeRadio}
                    items={(question as ChoiceQuestion).items}
                />
            );
        case QuestionType.OneRow:
            return (
                <RadioQuestion
                    {...commonProps}
                    value={(field.value as string[])?.[0]}
                    onUpdate={onChangeRadio}
                    items={(question as ChoiceQuestion).items}
                    view={RadioQuestionView.OneRow}
                />
            );
        case QuestionType.Checkbox:
            return (
                <CheckboxQuestion
                    {...commonProps}
                    values={field.value as string[]}
                    onUpdate={field.onChange}
                    items={(question as ChoiceQuestion).items}
                />
            );
        case QuestionType.Dropdown:
            return (
                <DropdownQuestion
                    {...commonProps}
                    value={field.value as string[]}
                    items={(question as ChoiceQuestion).items}
                    showFirst={(question as DropdownQuestionModel).show_first}
                    suggested={(question as DropdownQuestionModel).suggested}
                    onUpdate={field.onChange}
                />
            );
        case QuestionType.Suggest:
            return (
                <SuggestQuestion
                    {...commonProps}
                    value={field.value as string[]}
                    onUpdate={field.onChange}
                    multiple={(question as SuggestQuestionModel).multichoice}
                    getItems={getItems}
                    getItem={getItem}
                    onChange={field.onChange}
                    parents={parentsValues}
                    text={''}
                />
            );
        case QuestionType.Boolean:
            return (
                <BooleanQuestion
                    {...commonProps}
                    checked={Boolean(field.value)}
                    onUpdate={field.onChange}
                />
            );
        case QuestionType.Matrix: {
            const value = ((field.value || []) as MatrixValue[]).reduce<Record<string, string>>(
                (acc, item) => {
                    acc[item.row] = item.column;
                    return acc;
                },
                {},
            );

            return (
                <MatrixQuestion
                    {...commonProps}
                    value={value}
                    onChange={onChangeMatrix}
                    rows={(question as MatrixQuestionModel).rows}
                    columns={(question as MatrixQuestionModel).columns}
                />
            );
        }
        case QuestionType.File: {
            let maxCount = 1;
            let maxSize = 20;
            const maxCountValidation = question.validations?.find(
                (validation): validation is MaxCountFileValidation =>
                    validation.type === ValidationType.Count,
            );
            if (maxCountValidation) {
                maxCount = maxCountValidation.value;
            }
            const maxSizeValidation = question.validations?.find(
                (validation): validation is MaxSizeFileValidation =>
                    validation.type === ValidationType.Size,
            );
            if (maxSizeValidation) {
                maxSize = maxSizeValidation.value;
            }

            return (
                <FileQuestion
                    {...commonProps}
                    value={field.value as FileValue[]}
                    upload={upload}
                    onLoad={field.onChange}
                    maxCount={maxCount}
                    buttonIcon={<ArrowUpFromSquare aria-hidden="true" focusable="false" />}
                    buttonText={i18n('survey-page', 'upload-text')}
                    hint={i18n('survey-page', 'file-limit-hint', {
                        count: maxCount,
                        maxCount,
                        maxSize,
                    })}
                />
            );
        }
        case QuestionType.Integer:
            return (
                <IntegerQuestion
                    {...commonProps}
                    value={String(field.value)}
                    onUpdate={onChangeInteger}
                />
            );
        case QuestionType.Date:
        case QuestionType.DateRange: {
            return (
                <DateQuestion
                    {...commonProps}
                    value={field.value as string | DateRangeValue}
                    onUpdate={field.onChange}
                    range={question.type === QuestionType.DateRange}
                    max={
                        question.validations?.find(
                            (validation): validation is MaxDateValidation =>
                                validation.type === ValidationType.Max,
                        )?.value
                    }
                    min={
                        question.validations?.find(
                            (validation): validation is MinDateValidation =>
                                validation.type === ValidationType.Min,
                        )?.value
                    }
                />
            );
        }
        case QuestionType.Payment:
            return (
                <PaymentQuestion
                    {...commonProps}
                    value={String(field.value)}
                    onUpdate={onChangeInteger}
                    hint={i18n('survey-page', 'payment-hint')}
                    currency={i18n('survey-page', 'currency')}
                    currencyName={i18n('survey-page', 'sr-currency')}
                    disabled={(question as PaymentQuestionModel).fixed}
                />
            );
        case QuestionType.Captcha:
            return (
                <CaptchaQuestion
                    {...commonProps}
                    value={(field.value as CaptchaValue)?.value}
                    onUpdate={field.onChange}
                    url={(question as CaptchaQuestionModel).url}
                    captchaKey={(question as CaptchaQuestionModel).key}
                    refreshCaptcha={onRefreshCaptcha}
                />
            );
        case QuestionType.String: {
            let getStringItems:
                | ((text: string) => SuggestItem[] | Promise<SuggestItem[]>)
                | undefined = (question as StringQuestion).hint && getItems;
            if ((question as StringQuestion).hint?.items) {
                getStringItems = (text: string) =>
                    (question as StringQuestion).hint?.items?.reduce<SuggestItem[]>((acc, item) => {
                        if (!text || item.label.toLowerCase().includes(text.toLowerCase())) {
                            acc.push({
                                id: item.id,
                                text: item.label,
                                layer: DataSource.Choice,
                            });
                        }
                        return acc;
                    }, []) ?? [];
            }
            return (
                <TextQuestion
                    {...commonProps}
                    value={field.value as string}
                    onUpdate={field.onChange}
                    multiline={(question as StringQuestion).multiline}
                    getItems={getStringItems}
                />
            );
        }
        case QuestionType.Comment: {
            const {ref: _, ...defaultProps} = commonProps;
            return (
                <QuestionLayout
                    {...defaultProps}
                    type={QuestionLayoutType.Row}
                    positions={[
                        QuestionMarkupItem.Label,
                        QuestionMarkupItem.Comment,
                        QuestionMarkupItem.Image,
                    ]}
                    header={(question as CommentQuestion).header}
                />
            );
        }
        default: {
            const {ref: _, ...defaultProps} = commonProps;
            return <QuestionLayout {...defaultProps} type={QuestionLayoutType.Row} />;
        }
    }
};
export default QuestionSwitch;
